import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { contactLink, primaryNav } from "../data/navigation";

function navIsActive(to, pathname, hash) {
  if (to.startsWith("/#")) {
    return pathname === "/" && hash === to.slice(1);
  }
  const path = to.split("#")[0];
  if (path === "/") return pathname === "/";
  return pathname === path || pathname.startsWith(`${path}/`);
}

function itemIsActive(item, pathname, hash) {
  if (navIsActive(item.to, pathname, hash)) return true;
  return item.children?.some((child) => navIsActive(child.to, pathname, hash)) ?? false;
}

const navLinkClass = (active) =>
  `inline-flex items-center text-[13px] font-medium leading-none tracking-wide transition-colors hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gray-900 ${
    active ? "text-gray-900" : "text-gray-600"
  }`;

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
    setOpenMenu(null);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!openMenu) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest("[data-nav-menu]")) return;
      setOpenMenu(null);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenMenu(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenu]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-50 border-b border-gray-200 bg-white/95 backdrop-blur-md">
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-3 justify-self-start" discover="none">
          <img
            src="/mag-logo-hd-nobg.png"
            alt="Manufacturing Automation Group"
            width="1536"
            height="1024"
            className="h-12 w-auto sm:h-14"
          />
          <span className="sr-only sm:not-sr-only sm:text-sm sm:font-semibold sm:leading-tight sm:text-gray-900">
            Manufacturing Automation Group
          </span>
        </Link>

        <nav className="hidden items-center gap-6 justify-self-center lg:flex xl:gap-7" aria-label="Primary">
          {primaryNav.map((item) =>
            item.children?.length ? (
              <div
                key={item.label}
                data-nav-menu
                className="relative flex items-center"
                onMouseEnter={() => setOpenMenu(item.label)}
                onMouseLeave={() => setOpenMenu(null)}
                onBlur={(event) => {
                  if (!event.currentTarget.contains(event.relatedTarget)) setOpenMenu(null);
                }}
              >
                <NavLink
                  to={item.to}
                  discover="none"
                  className={navLinkClass(itemIsActive(item, location.pathname, location.hash))}
                  aria-current={navIsActive(item.to, location.pathname, location.hash) ? "page" : undefined}
                  aria-expanded={openMenu === item.label}
                  aria-haspopup="true"
                  onFocus={() => setOpenMenu(item.label)}
                  onClick={() => setOpenMenu(null)}
                >
                  {item.label}
                </NavLink>
                <div className={`absolute left-0 top-full z-50 pt-2 ${openMenu === item.label ? "block" : "hidden"}`}>
                  <div
                    className="border-t border-gray-900 bg-white/95 py-2 shadow-[0_18px_40px_-24px_rgba(17,24,39,0.45)] backdrop-blur-md"
                    aria-label={`${item.label} pages`}
                  >
                    {item.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        discover="none"
                        className={() =>
                          `block whitespace-nowrap px-4 py-1.5 text-[13px] font-medium tracking-wide transition-colors hover:text-gray-900 ${
                            navIsActive(child.to, location.pathname, location.hash)
                              ? "text-gray-900"
                              : "text-gray-500"
                          }`
                        }
                        aria-current={
                          navIsActive(child.to, location.pathname, location.hash) ? "page" : undefined
                        }
                        onClick={() => setOpenMenu(null)}
                      >
                        {child.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink
                key={item.label}
                to={item.to}
                discover="none"
                className={navLinkClass(navIsActive(item.to, location.pathname, location.hash))}
                aria-current={navIsActive(item.to, location.pathname, location.hash) ? "page" : undefined}
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="col-start-3 flex items-center justify-end gap-2 justify-self-end">
          <Link to={contactLink.to} className="btn-primary-dark hidden sm:inline-flex" discover="none">
            {contactLink.label}
          </Link>

          <button
            type="button"
            className="inline-flex h-11 w-11 items-center justify-center text-gray-800 lg:hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.75}
                d={menuOpen ? "M6 18L18 6M6 6l12 12" : "M4 7h16M4 12h16M4 17h16"}
              />
            </svg>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          id="mobile-nav"
          className="border-t border-gray-200 bg-white lg:hidden"
        >
          <nav className="flex flex-col px-4 py-4 sm:px-6 lg:px-8" aria-label="Mobile">
            {primaryNav.map((item) => (
              <div key={item.label} className="border-b border-gray-100">
                <Link
                  to={item.to}
                  discover="none"
                  className="block py-3 text-sm font-medium text-gray-800"
                >
                  {item.label}
                </Link>
                {item.children?.map((child) => (
                  <Link
                    key={child.to}
                    to={child.to}
                    discover="none"
                    className="block py-2 pl-4 text-sm font-medium text-gray-600"
                  >
                    {child.label}
                  </Link>
                ))}
              </div>
            ))}
            <Link to={contactLink.to} className="btn-primary-dark mt-4 w-full" discover="none">
              {contactLink.label}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
